// Centralized configuration for Bridge API supported countries
// This is the single source of truth for all supported countries

export interface SupportedCountry {
  code: string;
  name: string;
  status: 'active' | 'testing' | 'inactive';
  rails: ('SEPA' | 'WIRE' | 'ACH' | 'SPEI' | 'PIX')[]; // PIX for BRL requires Pix Endorsement
  currencies: string[];
  notes?: string;
}

export interface PopularBank {
  name: string;
  swift?: string;
  country: string;
  notes?: string;
}

// Comprehensive list of all countries supported by Bridge API
// Bridge supports WIRE transfers to all UN-recognized countries except sanctioned ones
// Supported currencies: USD, EUR, MXN, BRL (BRL requires Pix Endorsement from Bridge API)
export const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  // ACH Countries (US only)
  {
    code: 'US',
    name: 'United States',
    status: 'active',
    rails: ['ACH', 'WIRE'],
    currencies: ['USD'],
  },
  
  // SPEI Countries (Mexico only)
  {
    code: 'MX',
    name: 'Mexico',
    status: 'active',
    rails: ['SPEI', 'WIRE'],
    currencies: ['MXN'],
  },
  
  // SEPA Countries (Eurozone) - 20 countries
  {
    code: 'AD',
    name: 'Andorra',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'AT',
    name: 'Austria',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'BE',
    name: 'Belgium',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'CY',
    name: 'Cyprus',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'EE',
    name: 'Estonia',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'FI',
    name: 'Finland',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'FR',
    name: 'France',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'DE',
    name: 'Germany',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'GR',
    name: 'Greece',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'IE',
    name: 'Ireland',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'IT',
    name: 'Italy',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'LV',
    name: 'Latvia',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'LT',
    name: 'Lithuania',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'LU',
    name: 'Luxembourg',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'MT',
    name: 'Malta',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'MC',
    name: 'Monaco',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'NL',
    name: 'Netherlands',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'PT',
    name: 'Portugal',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'SM',
    name: 'San Marino',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'SK',
    name: 'Slovakia',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'SI',
    name: 'Slovenia',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'ES',
    name: 'Spain',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  {
    code: 'VA',
    name: 'Vatican City',
    status: 'active',
    rails: ['SEPA', 'WIRE'],
    currencies: ['EUR'],
  },
  
  // WIRE-only countries (All other non-sanctioned countries with USD)
  // Popular countries first
  {
    code: 'GB',
    name: 'United Kingdom',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CA',
    name: 'Canada',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AU',
    name: 'Australia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'JP',
    name: 'Japan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CH',
    name: 'Switzerland',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NO',
    name: 'Norway',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SE',
    name: 'Sweden',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'DK',
    name: 'Denmark',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SG',
    name: 'Singapore',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'HK',
    name: 'Hong Kong',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KR',
    name: 'South Korea',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'IL',
    name: 'Israel',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  
  // All other countries alphabetically (A-Z, excluding sanctioned ones)
  {
    code: 'AR',
    name: 'Argentina',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AM',
    name: 'Armenia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AZ',
    name: 'Azerbaijan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BH',
    name: 'Bahrain',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BR',
    name: 'Brazil',
    status: 'active',
    rails: ['WIRE', 'PIX'], // PIX for BRL requires Pix Endorsement
    currencies: ['USD', 'BRL'], // BRL requires Pix Endorsement from Bridge API
    notes: 'BRL (PIX) requires Pix Endorsement. If customer has base endorsement, Bridge can provide Pix endorsement.'
  },
  {
    code: 'BG',
    name: 'Bulgaria',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CL',
    name: 'Chile',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CO',
    name: 'Colombia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CR',
    name: 'Costa Rica',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'HR',
    name: 'Croatia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'DO',
    name: 'Dominican Republic',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'EG',
    name: 'Egypt',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SV',
    name: 'El Salvador',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'FJ',
    name: 'Fiji',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GE',
    name: 'Georgia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GH',
    name: 'Ghana',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GT',
    name: 'Guatemala',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'HN',
    name: 'Honduras',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'HU',
    name: 'Hungary',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'IS',
    name: 'Iceland',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'IN',
    name: 'India',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ID',
    name: 'Indonesia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'JM',
    name: 'Jamaica',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'JO',
    name: 'Jordan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KZ',
    name: 'Kazakhstan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KW',
    name: 'Kuwait',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LB',
    name: 'Lebanon',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LY',
    name: 'Libya',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MK',
    name: 'North Macedonia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MY',
    name: 'Malaysia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MV',
    name: 'Maldives',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MR',
    name: 'Mauritania',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MU',
    name: 'Mauritius',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MD',
    name: 'Moldova',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ME',
    name: 'Montenegro',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MA',
    name: 'Morocco',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NP',
    name: 'Nepal',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NG',
    name: 'Nigeria',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'OM',
    name: 'Oman',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PK',
    name: 'Pakistan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PA',
    name: 'Panama',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PY',
    name: 'Paraguay',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PE',
    name: 'Peru',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PH',
    name: 'Philippines',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PL',
    name: 'Poland',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'QA',
    name: 'Qatar',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'RO',
    name: 'Romania',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'RS',
    name: 'Serbia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SN',
    name: 'Senegal',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SL',
    name: 'Sierra Leone',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ZA',
    name: 'South Africa',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LK',
    name: 'Sri Lanka',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TW',
    name: 'Taiwan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TZ',
    name: 'Tanzania',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TH',
    name: 'Thailand',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TT',
    name: 'Trinidad and Tobago',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TN',
    name: 'Tunisia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TR',
    name: 'Turkey',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'UG',
    name: 'Uganda',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'UY',
    name: 'Uruguay',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'VN',
    name: 'Vietnam',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ZM',
    name: 'Zambia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  
  // European countries outside Eurozone
  {
    code: 'AL',
    name: 'Albania',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BA',
    name: 'Bosnia and Herzegovina',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BY',
    name: 'Belarus',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LI',
    name: 'Liechtenstein',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MO',
    name: 'Macau',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'XK',
    name: 'Kosovo',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  
  // Additional countries
  {
    code: 'AF',
    name: 'Afghanistan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'DZ',
    name: 'Algeria',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AO',
    name: 'Angola',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AG',
    name: 'Antigua and Barbuda',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BD',
    name: 'Bangladesh',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BB',
    name: 'Barbados',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BZ',
    name: 'Belize',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BJ',
    name: 'Benin',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BT',
    name: 'Bhutan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BO',
    name: 'Bolivia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BW',
    name: 'Botswana',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BN',
    name: 'Brunei',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BF',
    name: 'Burkina Faso',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BI',
    name: 'Burundi',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CV',
    name: 'Cabo Verde',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KH',
    name: 'Cambodia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CM',
    name: 'Cameroon',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CF',
    name: 'Central African Republic',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TD',
    name: 'Chad',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KM',
    name: 'Comoros',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CG',
    name: 'Congo',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CD',
    name: 'Democratic Republic of the Congo',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CK',
    name: 'Cook Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CI',
    name: "Côte d'Ivoire",
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'DJ',
    name: 'Djibouti',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'DM',
    name: 'Dominica',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TL',
    name: 'East Timor',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'EC',
    name: 'Ecuador',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GQ',
    name: 'Equatorial Guinea',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ER',
    name: 'Eritrea',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SZ',
    name: 'Eswatini',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ET',
    name: 'Ethiopia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PW',
    name: 'Palau',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PG',
    name: 'Papua New Guinea',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'FM',
    name: 'Micronesia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MH',
    name: 'Marshall Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KI',
    name: 'Kiribati',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NR',
    name: 'Nauru',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TO',
    name: 'Tonga',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TV',
    name: 'Tuvalu',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'VU',
    name: 'Vanuatu',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'WS',
    name: 'Samoa',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SB',
    name: 'Solomon Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GA',
    name: 'Gabon',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GM',
    name: 'Gambia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GD',
    name: 'Grenada',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GN',
    name: 'Guinea',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GW',
    name: 'Guinea-Bissau',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GY',
    name: 'Guyana',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'HT',
    name: 'Haiti',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KE',
    name: 'Kenya',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LA',
    name: 'Laos',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LS',
    name: 'Lesotho',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LR',
    name: 'Liberia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MG',
    name: 'Madagascar',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MW',
    name: 'Malawi',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ML',
    name: 'Mali',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MN',
    name: 'Mongolia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MZ',
    name: 'Mozambique',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MM',
    name: 'Myanmar',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NA',
    name: 'Namibia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NE',
    name: 'Niger',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NU',
    name: 'Niue',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'RW',
    name: 'Rwanda',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KN',
    name: 'Saint Kitts and Nevis',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'LC',
    name: 'Saint Lucia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'VC',
    name: 'Saint Vincent and the Grenadines',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ST',
    name: 'São Tomé and Príncipe',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SC',
    name: 'Seychelles',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SO',
    name: 'Somalia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SS',
    name: 'South Sudan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SD',
    name: 'Sudan',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SR',
    name: 'Suriname',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TG',
    name: 'Togo',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TK',
    name: 'Tokelau',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TC',
    name: 'Turks and Caicos Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'UA',
    name: 'Ukraine',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'VE',
    name: 'Venezuela',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'YE',
    name: 'Yemen',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'ZW',
    name: 'Zimbabwe',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  
  // Additional major countries and territories
  {
    code: 'AS',
    name: 'American Samoa',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AI',
    name: 'Anguilla',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'AW',
    name: 'Aruba',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BM',
    name: 'Bermuda',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'IO',
    name: 'British Indian Ocean Territory',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'VG',
    name: 'British Virgin Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'KY',
    name: 'Cayman Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CX',
    name: 'Christmas Island',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CC',
    name: 'Cocos Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'CW',
    name: 'Curaçao',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'FK',
    name: 'Falkland Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'FO',
    name: 'Faroe Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GF',
    name: 'French Guiana',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PF',
    name: 'French Polynesia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'TF',
    name: 'French Southern Territories',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GI',
    name: 'Gibraltar',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GL',
    name: 'Greenland',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GP',
    name: 'Guadeloupe',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GU',
    name: 'Guam',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GG',
    name: 'Guernsey',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'HM',
    name: 'Heard Island and McDonald Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'IM',
    name: 'Isle of Man',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'JE',
    name: 'Jersey',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MQ',
    name: 'Martinique',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'YT',
    name: 'Mayotte',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MS',
    name: 'Montserrat',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NC',
    name: 'New Caledonia',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'NF',
    name: 'Norfolk Island',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MP',
    name: 'Northern Mariana Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PN',
    name: 'Pitcairn',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PR',
    name: 'Puerto Rico',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'RE',
    name: 'Réunion',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'BL',
    name: 'Saint Barthélemy',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SH',
    name: 'Saint Helena, Ascension and Tristan da Cunha',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'MF',
    name: 'Saint Martin',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'PM',
    name: 'Saint Pierre and Miquelon',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SX',
    name: 'Sint Maarten',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'GS',
    name: 'South Georgia and the South Sandwich Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'SJ',
    name: 'Svalbard and Jan Mayen',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'UM',
    name: 'United States Minor Outlying Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'VI',
    name: 'U.S. Virgin Islands',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
  {
    code: 'WF',
    name: 'Wallis and Futuna',
    status: 'active',
    rails: ['WIRE'],
    currencies: ['USD'],
  },
];

// Popular countries for better UX (shown first in dropdowns)
export const POPULAR_COUNTRIES = [
  'US', 'GB', 'DE', 'FR', 'CA', 'AU', 'JP', 'CH', 'NL', 'ES', 'IT', 'SE', 'NO', 'DK', 'SG', 'HK', 'MX'
];

// Expanded bank database with real banks for all supported countries
export const POPULAR_BANKS: PopularBank[] = [
  // Germany - Active
  { name: 'Deutsche Bank', swift: 'DEUTDEFF', country: 'DE' },
  { name: 'Commerzbank', swift: 'COBADEFF', country: 'DE' },
  { name: 'DZ Bank', swift: 'GENODED1DGZ', country: 'DE' },
  { name: 'Hypovereinsbank', swift: 'HYVEDEMMXXX', country: 'DE' },
  { name: 'Postbank', swift: 'PBNKDEFF', country: 'DE' },
  
  // United Kingdom - Active
  { name: 'Barclays Bank', swift: 'BARCGB22', country: 'GB' },
  { name: 'HSBC UK', swift: 'HBUKGB4B', country: 'GB' },
  { name: 'Lloyds Bank', swift: 'LOYDGB2L', country: 'GB' },
  { name: 'NatWest', swift: 'NWBKGB2L', country: 'GB' },
  { name: 'Santander UK', swift: 'ABBYGB2L', country: 'GB' },
  
  // Cyprus - Testing
  { name: 'Bank of Cyprus', swift: 'BCYPCY2N', country: 'CY', notes: 'Largest bank in Cyprus' },
  { name: 'Hellenic Bank', swift: 'HEBACY2N', country: 'CY', notes: 'Second largest bank in Cyprus' },
  { name: 'Alpha Bank Cyprus', swift: 'ALPBCY2X', country: 'CY' },
  { name: 'Astrobank', swift: 'ASTRCY2N', country: 'CY' },
  { name: 'RCB Bank', swift: 'RCBCCY2N', country: 'CY' },
  
  // France - Testing
  { name: 'BNP Paribas', swift: 'BNPAFRPP', country: 'FR' },
  { name: 'Crédit Agricole', swift: 'AGRIFRPP', country: 'FR' },
  { name: 'Société Générale', swift: 'SOGEFRPP', country: 'FR' },
  { name: 'Crédit Mutuel', swift: 'CMCIFR2A', country: 'FR' },
  { name: 'La Banque Postale', swift: 'PSSTFRPP', country: 'FR' },
  
  // Italy - Testing
  { name: 'UniCredit', swift: 'UNCRITM1', country: 'IT' },
  { name: 'Intesa Sanpaolo', swift: 'BCITITMM', country: 'IT' },
  { name: 'Banco BPM', swift: 'BAPPIT21', country: 'IT' },
  { name: 'Monte dei Paschi di Siena', swift: 'PASCITM1', country: 'IT' },
  
  // Spain - Testing
  { name: 'Banco Santander', swift: 'BSCHESMM', country: 'ES' },
  { name: 'BBVA', swift: 'BBVAESMM', country: 'ES' },
  { name: 'CaixaBank', swift: 'CAIXESBB', country: 'ES' },
  { name: 'Banco Sabadell', swift: 'BSABESBB', country: 'ES' },
  
  // Netherlands - Testing
  { name: 'ING Bank', swift: 'INGBNL2A', country: 'NL' },
  { name: 'ABN AMRO', swift: 'ABNANL2A', country: 'NL' },
  { name: 'Rabobank', swift: 'RABONL2U', country: 'NL' },
  
  // United States - Active (Top 35 banks)
  { name: 'JPMorgan Chase', swift: 'CHASUS33', country: 'US' },
  { name: 'Bank of America', swift: 'BOFAUS3N', country: 'US' },
  { name: 'Wells Fargo', swift: 'WFBIUS6S', country: 'US' },
  { name: 'Citibank', swift: 'CITIUS33', country: 'US' },
  { name: 'U.S. Bancorp', swift: 'USBKUS44', country: 'US' },
  { name: 'Truist Bank', swift: 'SUNTUS33', country: 'US' },
  { name: 'PNC Bank', swift: 'PNCCUS33', country: 'US' },
  { name: 'Goldman Sachs Bank', swift: 'GSCCUS33', country: 'US' },
  { name: 'TD Bank', swift: 'NRTHUS33', country: 'US' },
  { name: 'Capital One', swift: 'HIBKUS44', country: 'US' },
  { name: 'Morgan Stanley', swift: 'MSLNUS33', country: 'US' },
  { name: 'State Street Bank', swift: 'SBOSUS33', country: 'US' },
  { name: 'American Express Bank', swift: 'AECBUS33', country: 'US' },
  { name: 'HSBC Bank USA', swift: 'MRMDUS33', country: 'US' },
  { name: 'Fifth Third Bank', swift: 'FTBCUS3C', country: 'US' },
  { name: 'Regions Bank', swift: 'REGNUS44', country: 'US' },
  { name: 'M&T Bank', swift: 'MNTRUS33', country: 'US' },
  { name: 'KeyBank', swift: 'KEYBUS33', country: 'US' },
  { name: 'Huntington Bank', swift: 'HUNTUS33', country: 'US' },
  { name: 'Citizens Bank', swift: 'CTZNUS33', country: 'US' },
  { name: 'Comerica Bank', swift: 'MNBDUS33', country: 'US' },
  { name: 'Zions Bank', swift: 'ZBOCUS44', country: 'US' },
  { name: 'First Citizens Bank', swift: 'FCBFUS66', country: 'US' },
  { name: 'Synovus Bank', swift: 'SYNVUS33', country: 'US' },
  { name: 'Valley National Bank', swift: 'VNBKUS33', country: 'US' },
  { name: 'Frost Bank', swift: 'FRSTUS44', country: 'US' },
  { name: 'First Horizon Bank', swift: 'FHNBUS44', country: 'US' },
  { name: 'Webster Bank', swift: 'WBKNUS33', country: 'US' },
  { name: 'Associated Bank', swift: 'ABOKUS44', country: 'US' },
  { name: 'BankUnited', swift: 'BUNUS44', country: 'US' },
  { name: 'Popular Bank', swift: 'POPRUS44', country: 'US' },
  { name: 'First National Bank', swift: 'FNBUS44', country: 'US' },
  { name: 'Old National Bank', swift: 'OLNUS44', country: 'US' },
  { name: 'Bank OZK', swift: 'OZARUS44', country: 'US' },
  { name: 'UMB Bank', swift: 'UMBKUS44', country: 'US' },
  { name: 'East West Bank', swift: 'EWBKUS66', country: 'US' },
  
  // Mexico - Active
  { name: 'BBVA México', swift: 'BCMRMXMM', country: 'MX' },
  { name: 'Banamex', swift: 'BNMXMXMM', country: 'MX' },
  { name: 'Santander México', swift: 'BMSXMXMM', country: 'MX' },
];

// Helper functions
export const getCountriesByRail = (rail: string) => 
  SUPPORTED_COUNTRIES.filter(country => country.rails.includes(rail as any));

export const getActiveCountries = () => 
  SUPPORTED_COUNTRIES.filter(country => country.status === 'active');

export const getCountryByCode = (code: string) => 
  SUPPORTED_COUNTRIES.find(country => country.code === code);

export const getBanksByCountry = (countryCode: string) => 
  POPULAR_BANKS.filter(bank => bank.country === countryCode);

export const getBanksByRail = (rail: string) => {
  const supportedCountries = getCountriesByRail(rail);
  const countryCodes = supportedCountries.map(c => c.code);
  return POPULAR_BANKS.filter(bank => countryCodes.includes(bank.country));
};

export const getCurrenciesByRail = (rail: string) => {
  const countries = getCountriesByRail(rail);
  return [...new Set(countries.flatMap(country => country.currencies))];
};

export const isCountrySupported = (countryCode: string, rail?: string) => {
  const country = getCountryByCode(countryCode);
  if (!country) return false;
  if (!rail) return true;
  return country.rails.includes(rail as any);
};

// Status display helpers
export const getStatusColor = (status: SupportedCountry['status']) => {
  switch (status) {
    case 'active': return 'text-success';
    case 'testing': return 'text-warning';
    case 'inactive': return 'text-gray-400';
    default: return 'text-gray-400';
  }
};

export const getStatusLabel = (status: SupportedCountry['status']) => {
  switch (status) {
    case 'active': return 'Fully supported';
    case 'testing': return 'Fully supported';
    case 'inactive': return 'Not available';
    default: return 'Fully supported';
  }
};